import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';
import { SMZR_STAT_DRAWER_DEFAULT_TEXT, SUMMARIZER_STATS } from '@Common/constants';
import { getWordCount } from '@Common/handlers/helper';
import { Stat } from '@Common/models';
import { Output, SummarizerStats } from '../models/store';
import { TransformedSummarizationResultInAbs } from '../models/transformation';

const StatisticsDrawer = dynamic(() => import('src/_common/components/drawer/StatisticsDrawer'), {
  ssr: false,
});

const SummarizerStastDrawer = () => {
  const [stats, setStats] = useState<Stat[]>([]);
  const [showStats, setShowStats] = useState(false);
  const isQuilling: boolean = useSelector((state: RootState) => state?.smzr?.controls?.isQuilling);
  const summaryMode: string = useSelector((state: RootState) => state?.smzr?.controls?.summaryMode);
  const absLengthGauge: number = useSelector(
    (state: RootState) => state?.smzr?.controls?.absLengthGauge
  );
  const extLengthGauge: number = useSelector(
    (state: RootState) => state?.smzr?.controls?.extLengthGauge
  );
  const summaryOutput: Output = useSelector((state: RootState) => state?.smzr?.output);

  const calculateStats = () => {
    if (isQuilling) return;
    if (summaryMode === 'abs' && !summaryOutput[`abs~${absLengthGauge}`]) {
      setShowStats(false);
      return;
    }

    const statValues: SummarizerStats = {
      words: { from: getWordCount(summaryOutput.origText), to: 0 },
      sentences: { from: 0, to: 0 },
      characters: { from: summaryOutput.origText.length, to: 0 },
      reduction: '-',
    };

    statValues.sentences.from = summaryOutput.origNumberOfSentences;

    if (summaryMode === 'abs') {
      const absSummary = summaryOutput[
        `abs~${absLengthGauge}`
      ] as TransformedSummarizationResultInAbs;
      if (absSummary) {
        statValues.sentences.to = absSummary.numberOfSentences;
        statValues.words.to = getWordCount(absSummary.summaryPara);
        statValues.characters.to = absSummary.summaryPara.length;
      }
    } else if (summaryMode === 'ext') {
      const extSummary = summaryOutput.ext;
      statValues.sentences.to = extSummary.summarySentenceSequence.length;
      let combinedText = '';
      const allSentences = extSummary.sentences;
      extSummary.summarySentenceSequence.forEach((sentenceId: keyof typeof allSentences) => {
        combinedText += `${allSentences[sentenceId].text} `;
      });
      statValues.words.to = getWordCount(combinedText);
      statValues.characters.to = combinedText.length;
    }

    const percentReduction =
      statValues.words.to && statValues.words.from
        ? ((statValues.words.from - statValues.words.to) / statValues.words.from) * 100
        : 0;
    statValues.reduction = percentReduction >= 0 ? `${Math.round(percentReduction)}%` : '-';

    const statsArray: Stat[] = [];
    SUMMARIZER_STATS.forEach((stat) => {
      stat.value = statValues[stat.key];
      statsArray.push(stat);
    });
    setStats(statsArray);
    setShowStats(true);
  };

  useEffect(() => {
    calculateStats();
  }, [isQuilling, absLengthGauge, extLengthGauge, summaryMode, summaryOutput.origText]);

  return (
    <StatisticsDrawer
      stats={stats}
      showStats={showStats && !isQuilling}
      defaultText={SMZR_STAT_DRAWER_DEFAULT_TEXT}
    />
  );
};

export default SummarizerStastDrawer;
